/**
 * AllHighlights Page
 *
 * Displays all highlights for the user, grouped by article.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllHighlights } from '../services/highlights';
import { formatFullDate } from '../utils/dateUtils';

const AllHighlights = () => {
  const [highlightsData, setHighlightsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllHighlights();
  }, []);

  const fetchAllHighlights = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAllHighlights();
      setHighlightsData(data);
    } catch (err) {
      console.error('Error fetching all highlights:', err);
      setError(err.message || 'Failed to load highlights');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading highlights...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Highlights</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchAllHighlights}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const hasHighlights = highlightsData && highlightsData.total_highlights > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="mr-4 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">All Highlights</h1>
                <p className="text-sm text-gray-600 mt-1">
                  {highlightsData?.total_highlights || 0} highlights across {highlightsData?.total_articles || 0} articles
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!hasHighlights ? (
          <div className="text-center py-12">
            <svg
              className="w-16 h-16 text-gray-300 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Highlights Yet</h3>
            <p className="text-sm text-gray-600 mb-6">
              Start highlighting important passages in your articles
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Go to Articles
            </Link>
          </div>
        ) : (
          <div className="space-y-8">
            {highlightsData.articles.map((articleGroup) => (
              <div key={articleGroup.article_id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Article Header */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <Link
                    to={`/article/${articleGroup.article_id}`}
                    className="group"
                  >
                    <h2 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {articleGroup.article_title}
                    </h2>
                    <div className="flex items-center mt-2 text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      <span className="truncate">{articleGroup.article_url}</span>
                    </div>
                  </Link>
                  <div className="flex items-center mt-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      {articleGroup.highlight_count} {articleGroup.highlight_count === 1 ? 'highlight' : 'highlights'}
                    </span>
                  </div>
                </div>

                {/* Highlights List */}
                <div className="divide-y divide-gray-200">
                  {articleGroup.highlights.map((highlight) => (
                    <div
                      key={highlight.id}
                      className="px-6 py-4 hover:bg-gray-50 transition-colors"
                    >
                      <Link
                        to={`/article/${articleGroup.article_id}`}
                        className="block group"
                      >
                        {/* Highlighted Text */}
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 px-4 py-3 rounded-r-md mb-2">
                          <p className="text-gray-900 text-sm leading-relaxed group-hover:text-gray-700">
                            {highlight.exact_text}
                          </p>
                        </div>

                        {/* Metadata */}
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <div className="flex items-center space-x-3">
                            <time dateTime={highlight.created_at}>
                              {formatFullDate(highlight.created_at)}
                            </time>
                            {!highlight.is_active && (
                              <>
                                <span>•</span>
                                <span className="text-yellow-600 font-medium">Outdated</span>
                              </>
                            )}
                          </div>
                          <span className="text-blue-600 group-hover:text-blue-700 font-medium">
                            View in article →
                          </span>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllHighlights;
